import * as React from "react";
import { useNavigate } from "react-router";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import SpeedIcon from "@mui/icons-material/Speed";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import userMonitor from "../../userMonitor";

import { useHomeDupr } from "./HomeDuprContext";
import type { HomeRecommendation as Recommendation } from "./useHomeRecommendation";

/**
 * The "recommended next step" card: what the home screen leads with when a member
 * arrives back from Club-in-Club's onboarding, whose last button is "Book my assessment".
 *
 * Styled as one more library card — the same surface, radius and type as the rows below
 * it — with an accent outline and an eyebrow, so it reads as "this one, first" rather
 * than as a foreign banner. It sits under the widget, not in the top slot: the widget is
 * showing the points they have just unlocked, which is the other half of the message.
 *
 * Where it points follows the library's own assessment row: a member who has already
 * submitted an assessment is sent to their dashboard instead. The copy is marketing's
 * (Abby's edit to the design canvas), so it stays as written.
 */
export default function HomeRecommendation({
    recommendation,
}: {
    recommendation: Recommendation | null;
}): React.JSX.Element | null {
    const navigate = useNavigate();
    const [dismissed, setDismissed] = React.useState(false);
    const { hasSubmitted, loading } = useHomeDupr();

    // Withheld while DUPR standing is unknown, as the library row is, so the title
    // does not change under the member's finger.
    if (!recommendation || dismissed || loading) return null;

    const target = hasSubmitted
        ? {
              title: "See your DUPR Dashboard",
              subtitle: "Your rating, match history and reliability",
              navUrl: "/dupr-dashboard",
              icon: (
                  <LeaderboardIcon
                      sx={{ color: "#3CE97C", fontSize: "1.5rem" }}
                  />
              ),
          }
        : {
              title: "Get rated",
              subtitle:
                  "Take the 10-minute assessment. Volley's AI rates your game, then a real coach sends feedback and drills for your next session.",
              navUrl: "/journeys/long-form",
              icon: <SpeedIcon sx={{ color: "#3CE97C", fontSize: "1.5rem" }} />,
          };

    const go = () => {
        userMonitor.addAction("home_recommendation_selected", {
            recommendation,
            navUrl: target.navUrl,
        });
        navigate(target.navUrl, { state: { source: "membership_onboarding" } });
    };

    const dismiss = () => {
        userMonitor.addAction("home_recommendation_dismissed", {
            recommendation,
        });
        setDismissed(true);
    };

    return (
        <Box
            sx={{
                mb: 1.5,
                p: "12px 10px 10px",
                borderRadius: "12px",
                backgroundColor: "#243049",
                boxShadow: "inset 0 0 0 2px #3CE97C",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ pl: "45px" }}
            >
                <Typography
                    component="span"
                    sx={{
                        fontSize: "0.62rem",
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "#3CE97C",
                    }}
                >
                    You&apos;re in. Start here.
                </Typography>
                <IconButton
                    size="small"
                    aria-label="Dismiss recommendation"
                    onClick={dismiss}
                    sx={{ color: "#94a3b8", my: -1, mr: -0.5 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Stack>
            <Box
                component="button"
                type="button"
                onClick={go}
                sx={{
                    all: "unset",
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    cursor: "pointer",
                    color: "white",
                    textAlign: "left",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        width: 30,
                        display: "flex",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                    }}
                >
                    {target.icon}
                </Box>
                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "white",
                            fontWeight: 700,
                            fontSize: "1.1rem",
                        }}
                    >
                        {target.title}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "#94a3b8",
                            fontSize: "0.8rem",
                            fontWeight: 400,
                        }}
                    >
                        {target.subtitle}
                    </Typography>
                </Stack>
                <ChevronRightIcon sx={{ color: "#3CE97C", fontSize: 20 }} />
            </Box>
        </Box>
    );
}
